<template>
  <Teleport to="body">
    <div class="toast-container" aria-live="polite">
      <TransitionGroup name="toast">
        <div
          v-for="toast in toasts"
          :key="toast.id"
          :class="['toast', `toast-${toast.type}`, { 'toast-with-title': toast.title }]"
          role="alert"
        >
          <span class="toast-icon" aria-hidden="true">
            <svg v-if="toast.type === 'success'" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
              <path d="M8 12l3 3 5-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <svg v-else-if="toast.type === 'warning'" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
              <path d="M12 8v4M12 16h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
            <svg v-else viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
              <path d="M15 9l-6 6M9 9l6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </span>
          <div class="toast-content" :class="{ 'toast-content-with-title': toast.title }">
            <template v-if="toast.title">
              <div class="toast-header">
                <span class="toast-title">{{ toast.title }}</span>
                <button type="button" class="toast-close" @click="dismiss(toast.id)" aria-label="Fechar">
                  &times;
                </button>
              </div>
              <p class="toast-body">{{ toast.message }}</p>
            </template>
            <template v-else>
              <span class="toast-message">{{ toast.message }}</span>
              <button type="button" class="toast-close" @click="dismiss(toast.id)" aria-label="Fechar">
                &times;
              </button>
            </template>
          </div>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script setup>
import { useToast } from '../composables/useToast';

const { toasts, dismiss } = useToast();
</script>

<style lang="scss" scoped>
.toast-container {
  position: fixed;
  top: 24px;
  right: 24px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 420px;

  &:has(.toast-with-title) {
    max-width: 520px;
  }
}

.toast {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  border-radius: 12px;
  border: 1px solid;
  font-size: 15px;
  font-weight: 600;
  color: $black-primary;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);

  &.toast-with-title {
    align-items: flex-start;
    min-width: 480px;
  }
}

.toast-with-title .toast-icon {
  margin-top: 10px !important;
}

.toast-icon {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toast-icon svg {
  width: 100%;
  height: 100%;
}

.toast-content {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  background: transparent;

  &.toast-content-with-title {
    flex-direction: column;
    align-items: stretch;
    gap: 0;
  }
}

.toast-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.15);
  background: inherit;
}

.toast-title {
  flex: 1;
  min-width: 0;
  font-weight: 600;
  color: $black-primary;
  font-size: 15px;
}

.toast-body {
  margin: 0;
  font-weight: 500;
  font-size: 14px;
  line-height: 1.45;
  color: $black-primary;
}

.toast-message {
  flex: 1;
  min-width: 0;
}

.toast-close {
  flex-shrink: 0;
  background: none;
  border: none;
  font-size: 26px !important;
  line-height: 1;
  color: $gray-secondary;
  cursor: pointer;
  padding: 0 4px;
  margin: -4px -4px -4px 0;

  .toast-header & {
    margin: -4px 0 -4px 4px;
  }

  &:hover {
    color: $black-primary;
  }
}

.toast-success {
  background-color: #EBFFF3;
  border-color: $green-primary;

  .toast-icon {
    color: $green-primary;
  }
}

.toast-warning {
  background-color: $yellow-secondary;
  border-color: $yellow-primary;

  .toast-icon {
    color: darken($yellow-primary, 15%);
  }
}

.toast-error {
  background-color: #FCE8E7;
  border-color: $red-primary;

  .toast-icon {
    color: $red-primary;
  }
}

.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

.toast-move {
  transition: transform 0.3s ease;
}
</style>
